import classNames from 'classnames';
import React from 'react';
import Field from '../../Field';
import css from './SectionContainer.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { getListingsById } from '../../../../ducks/marketplaceData.duck';
import TrainingProgram from '../../../../components/TrainingProgram/TrainingProgram';
import HowItWork from '../../../../components/HowItWork/HowItWork';
import { useConfiguration } from '../../../../context/configurationContext';
import {
  createPopularListingsParams,
  fetchPopularListings,
} from '../../../LandingPage/LandingPage.duck';
import PayYourWay from '../../../../components/PayYourWay/PayYourWay';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This component can be used to wrap some common styles and features of Section-level components.
 * E.g: const SectionHero = props => (<SectionContainer><H1>Hello World!</H1></SectionContainer>);
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.id id of the section
 * @param {string?} props.as tag/element name. Defaults to 'section'.
 * @param {ReactNode} props.children
 * @param {Object} props.appearance
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} containing wrapper that can be used inside Block components.
 */
const SectionContainer = props => {
  const { className, rootClassName, id, as, children, appearance, options, ...otherProps } = props;
  const Tag = as || 'section';
  const classes = classNames(rootClassName || css.root, className);
  const dispatch = useDispatch();
  const config = useConfiguration();

  // Get ratings-based listings from LandingPage slice
  const {
    popularListingsIds,
    fetchPopularListingsInProgress,
    fetchPopularListingsError,
  } = useSelector(state => state.LandingPage || {});
  const popularListings = useSelector(state => getListingsById(state, popularListingsIds));

  const handlePopularCategoryChange = React.useCallback(
    categoryLevel1 => {
      if (!config) {
        return Promise.resolve();
      }

      return dispatch(
        fetchPopularListings({
          searchParams: createPopularListingsParams(config, categoryLevel1),
          config,
        })
      ).unwrap();
    },
    [config, dispatch]
  );

  return (
    <Tag className={classes} id={id} {...otherProps}>
      {id === 'hero-section-container' && (
        <div className={css.heroSectionHeading}>
          <h1>
            Apprenez des <span>compétences réelles</span>. Formez-vous près de chez vous.
          </h1>
          <p>
            Découvrez des programmes de formation vérifiés près de chez vous. Comparez, choisissez
            et réservez un apprentissage pratique en quelques clics.
          </p>
        </div>
      )}

      {appearance?.fieldType === 'customAppearance' ? (
        <Field
          data={{ alt: `Background image for ${id}`, ...appearance }}
          className={className}
          options={options}
      />
      ) : null}

      <div className={css.sectionContent}>
        {children}
        {id === 'training-program' && (
          <TrainingProgram
            listings={popularListings}
            fetchPopularListingsInProgress={fetchPopularListingsInProgress}
            fetchPopularListingsError={fetchPopularListingsError}
            onCategoryChange={handlePopularCategoryChange}
          />
        )}
        {id === 'how-it-works' && <HowItWork />}
        {id === 'pay-your-way' && <PayYourWay />}
      </div>
    </Tag>
  );
};

export default SectionContainer;
